import {
  Box,
  FocusLock,
  IconButton,
  useDisclosure,
  usePrevious,
} from "@chakra-ui/react";
import { isEqual } from "lodash-es";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Location, useLocation } from "react-router-dom";
import { NavCollapseIcon } from "shared/icons/NavCollapseIcon";
import { NavLocationState } from "shared/navigation/NavLocationState";
import zIndices from "theme/zIndices";
import { useBoolean, useDebounceValue, useTimeout } from "usehooks-ts";

export interface PageSidebarProps {
  defaultIsPersistent?: boolean;
  width?: number;
}

const SHOW_DELAY = 1_000;
const HIDE_DELAY = 500;

const PageSidebar: React.FC<React.PropsWithChildren<PageSidebarProps>> = ({
  defaultIsPersistent = true,
  width = 210,
  children,
}) => {
  const location: Location<NavLocationState | undefined> = useLocation();
  const prevLocation = usePrevious(location) as
    | Location<NavLocationState | undefined>
    | undefined;

  const {
    value: isPersistent,
    toggle: toggleIsPersistent,
    setValue: setIsPersistent,
  } = useBoolean(defaultIsPersistent);

  const { isOpen, onOpen, onClose } = useDisclosure({
    defaultIsOpen: true,
  });

  useLayoutEffect(() => {
    if (prevLocation !== undefined && !isEqual(prevLocation, location)) {
      // don't restore sidebar state when switching between page tabs
      const wasPageTab = prevLocation.state?.isPageTab;
      const isPageTab = location.state?.isPageTab;
      if (!isPageTab || wasPageTab !== isPageTab) {
        setIsPersistent(defaultIsPersistent);
      }
    }
  }, [defaultIsPersistent, location, prevLocation, setIsPersistent]);

  useLayoutEffect(() => {
    if (isPersistent) {
      onOpen();
    } else {
      onClose();
    }
  }, [isPersistent, onClose, onOpen]);

  return (
    <>
      <PageSidebarTrigger onTrigger={onOpen} />
      <PageSidebarOverlay
        isActive={!isPersistent && isOpen}
        onTrigger={onClose}
        width={width}
      />
      <PageSidebarToggle
        width={width}
        onClick={toggleIsPersistent}
        isPersistent={isPersistent}
        isOpen={isOpen}
        onMouseEnter={onOpen}
      />
      <PageSidebarBody
        width={width}
        isPersistent={isPersistent}
        isOpen={isOpen}
      >
        {children}
      </PageSidebarBody>
    </>
  );
};
export default PageSidebar;

/***
 * Invisible box covering entire page for hiding the sidebar.
 */
const PageSidebarOverlay: React.FC<{
  isActive: boolean;
  onTrigger: () => void;
  width: number;
}> = ({ isActive, onTrigger, width }) => {
  const [isMouseOver, setIsMouseOver] = useState(false);
  useEffect(() => {
    if (!isActive) {
      setIsMouseOver(false);
    }
  }, [isActive]);
  useTimeout(onTrigger, isActive && isMouseOver ? HIDE_DELAY : null);
  return isActive ? (
    <Box
      pos="fixed"
      top="64px"
      right={0}
      bottom={0}
      left={`${width}px`}
      zIndex={zIndices.sidebarOverlay}
      onClick={onTrigger}
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
    />
  ) : null;
};

const PageSidebarBody: React.FC<
  React.PropsWithChildren<{
    isPersistent: boolean;
    isOpen: boolean;
    width: number;
  }>
> = ({ isPersistent, isOpen, children, width }) => {
  const [shouldAnimate] = useDebounceValue(!isPersistent, 200);
  return (
    <Box
      data-ls-enabled={true}
      data-public={true}
      position={isPersistent ? undefined : "fixed"}
      transition={shouldAnimate ? "left 0.2s ease-in-out" : undefined}
      left={isOpen ? 0 : `-${width}px`}
      w={`${width}px`}
      h={isPersistent ? "100%" : undefined}
      top={isPersistent ? undefined : "64px"}
      bottom={isPersistent ? undefined : "0px"}
      paddingTop={isPersistent ? "1px" : undefined}
      overflowY="auto"
      boxShadow={isPersistent ? "none" : "md"}
      borderRightWidth={1}
      zIndex={zIndices.sidebarBody}
      bg="gray.25"
    >
      <FocusLock isDisabled={!isOpen || isPersistent}>{children}</FocusLock>
    </Box>
  );
};

/***
 * Floating arrow button to expand/collapse the sidebar
 */
const PageSidebarToggle: React.FC<{
  onClick: () => void;
  onMouseEnter: () => void;
  isOpen: boolean;
  isPersistent: boolean;
  width: number;
}> = ({ onClick, onMouseEnter, isOpen, isPersistent, width }) => {
  const { t } = useTranslation(["common"]);
  return (
    <Box
      className="mb-page-sidebar-toggle"
      position="absolute"
      marginTop="-12px"
      marginLeft={isOpen && isPersistent ? `${width - 20}px` : "4px"}
      zIndex={zIndices.sidebarToggle}
      onMouseEnter={onMouseEnter}
    >
      <IconButton
        aria-label={
          isPersistent ? t("common:hideSidebar") : t("common:showSidebar")
        }
        variant="unstyled"
        p="0 !important"
        icon={<NavCollapseIcon />}
        w="16px"
        h="16px"
        transform={isPersistent ? "rotate(0deg)" : "rotate(180deg)"}
        onClick={onClick}
        color="gray.400"
        _hover={{
          color: "gray.500",
        }}
        _active={{
          color: "gray.800",
        }}
      />
    </Box>
  );
};

/***
 * Invisible box along the left edge of the screen to trigger the sidebar
 */
const PageSidebarTrigger: React.FC<{
  onTrigger: () => void;
}> = ({ onTrigger }) => {
  const [isMouseOver, setIsMouseOver] = useState(false);
  useTimeout(onTrigger, isMouseOver ? SHOW_DELAY : null);
  return (
    <Box
      position="absolute"
      width="12px"
      height="calc(100vh - 64px)"
      top="0"
      marginTop="64px"
      zIndex={zIndices.sidebarTrigger}
      onMouseEnter={() => {
        if (document.hasFocus()) {
          setIsMouseOver(true);
        }
      }}
      onMouseLeave={() => setIsMouseOver(false)}
    />
  );
};
